<template>
  <GenericCard
    :locked="locked"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="v$.section.$invalid"
    :dirty="v$.section.$anyDirty"
    title="Demographics"
    :section="section"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <v-col>
          <v-autocomplete
            v-model="section.religion"
            :items="optionsReligion"
            label="Religion"
            outlined
            multiple
            :error-messages="getErrorMessages(v$.section.religion)"
            @input="v$.section.religion.$touch()"
            @blur="v$.section.religion.$touch()"
          />
        </v-col>
        <v-col>
          <v-autocomplete
            v-model="section.race"
            :items="optionsRace"
            label="Race"
            outlined
            multiple
            :error-messages="getErrorMessages(v$.section.race)"
            @input="v$.section.race.$touch()"
            @blur="v$.section.race.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-autocomplete
            v-model="section.ethnicity"
            :items="optionsEthnicity"
            label="Ethnicity"
            outlined
            multiple
            :error-messages="getErrorMessages(v$.section.ethnicity)"
            @input="v$.section.ethnicity.$touch()"
            @blur="v$.section.ethnicity.$touch()"
          />
        </v-col>
        <v-col>
          <v-autocomplete
            v-model="section.yearly_household_income_range"
            :items="optionsYearlyHouseholdIncomeRange"
            label="Household income range"
            outlined
            :error-messages="getErrorMessages(v$.section.yearly_household_income_range)"
            @input="v$.section.yearly_household_income_range.$touch()"
            @blur="v$.section.yearly_household_income_range.$touch()"
          />
        </v-col>
        <v-col>
          <v-autocomplete
            v-model="section.household_size"
            :items="optionsHouseholdSize"
            label="Household Size"
            outlined
            :error-messages="getErrorMessages(v$.section.household_size)"
            @input="v$.section.household_size.$touch()"
            @blur="v$.section.household_size.$touch()"
          />
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import { alertHandler } from '@/alertHandler'

import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'

export default {
  name: 'DemographicsCard',
  components: { GenericCard },

  props: {
    id: {
      type: String,
      required: true,
    },

    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },

  },

  setup () {
    return {
      v$: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      cached_a_number: null,
      cached_ssn: null,
      section: {},
    }
  },

  validations () {
    return {
      section: {
        ethnicity: {},
        household_size: {},
        race: {},
        religion: {},
        yearly_household_income_range: {},
      },
    }
  },

  computed: {
    aNumberLabel () {
      return this.section.no_a_number ? 'A# (None)' : 'A#'
    },

    cardIdentifier () { return 'demographics-card' },

    locked: {
      get () {
        return this.peDemographicsCardLock
      },

      set (value) {
        this.setPeDemographicsCardLock(value)
      },
    },

    optionsEthnicity () { return this.getCachedDropdown('parties/ethnicity') },
    optionsHouseholdSize () { return this.getCachedDropdown('parties/household_size') },
    optionsRace () { return this.getCachedDropdown('parties/race') },
    optionsReligion () { return this.getCachedDropdown('parties/religion') },
    optionsYearlyHouseholdIncomeRange () { return this.getCachedDropdown('parties/yearly_household_income_range') },

    ssnLabel () {
      return this.section.no_ssn ? 'SSN (None)' : 'SSN'
    },

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),

    ...mapGetters(PARTY_NAMESPACE, [
      'peDemographicsCardLock',
      'peParty',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },

    'section.no_a_number' (val) {
      if (val) {
        this.cached_a_number = this.section.a_number
        this.section.a_number = ''
      } else if (this.cached_a_number) {
        this.section.a_number = this.cached_a_number
        this.cached_a_number = null
      }
    },

    'section.no_ssn' (val) {
      if (val) {
        this.cached_ssn = this.section.ssn
        this.section.ssn = ''
      } else if (this.cached_ssn) {
        this.section.ssn = this.cached_ssn
        this.cached_ssn = null
      }
    },
  },

  created () {
    this.initPeDemographicsCard()
    this.getDropdownOptions({ path: 'parties/religion' })
    this.getDropdownOptions({ path: 'parties/race' })
    this.getDropdownOptions({ path: 'parties/ethnicity' })
    this.getDropdownOptions({ path: 'parties/yearly_household_income_range' })
    this.getDropdownOptions({ path: 'parties/household_size' })
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardRefresh () {
      return this.peDemographicsCardRefresh({ id: this.id })
    },

    async onCardUpdate () {
      return this.peDemographicsCardUpdate({ id: this.peParty.id, section: this.section })
    },

    async onSectionUnlock () {
      return this.setPeDemographicsCardLock(false)
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'setPeDemographicsCardLock',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeDemographicsCard',
      'peDemographicsCardRefresh',
      'peDemographicsCardUpdate',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
